'use strict';

/**
 * buildPlanName — canonical human display name for a sales plan, derived from
 * its period attributes + location.
 *
 * SSOT shared by planner-web's create wizard (the auto-name it writes to
 * `metadata.planName`) and the demo seed, so both produce identical names.
 * Verbatim port of the former web-only `computeAutoName` (planner-web
 * `useSalesPlanCreatePage`); co-located with `buildPlanSlug` (both derive a
 * label from the same plan attributes). Title-case month names here are
 * distinct from slug.js's lowercase `MONTH_ABBR` (which is for URL slugs).
 *
 * @module @puffin-core/id-generator/planName
 */

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Build the canonical display name for a sales plan.
 *
 * Single-period only: R-1 (PLN-PERIOD-LADDER-001) made the wizard create
 * exactly ONE plan per pass, so no caller carries a `periodCount` any more —
 * the former multi-period range legs (Apr – Jun 2026, Q1 – Q2 2026, …) are
 * deleted with the attribute.
 *
 * @param {Object} attrs - Plan period attributes (the create wizard's `form`).
 * @param {string} attrs.planType - `weekly` | `monthly` | `quarterly` | `annual`
 * @param {string} attrs.planCategory - e.g. `forecast` | `budget`
 * @param {number} attrs.planMonth - 1-12 (monthly / weekly)
 * @param {number} [attrs.displayMonth] - The civil month (1-12) the period MEANS,
 *   when that differs from `planMonth`. On a retail tenant `planMonth` is the
 *   fiscal PERIOD NUMBER — that is what `computePeriodDates` looks rows up by —
 *   and rendering it as a civil-month index is only correct for a
 *   January-anchored year. An NRF-style year anchored "closest to Feb 1" has a
 *   P1 that can START in late January while MEANING February, and named itself
 *   "January" as a result. Callers holding the fiscal rows resolve the true
 *   month (planner-web: `resolveFiscalMonthNumber`, the same rule the Step-1
 *   period selector and the Step-2 context labels use) and pass it here.
 *   Absent — every standard-calendar caller, and the demo seed — the behaviour
 *   is byte-identical to before.
 * @param {number} attrs.planYear - 4-digit year
 * @param {number} attrs.planQuarter - 1-4 (quarterly)
 * @param {number} attrs.planWeek - week-of-month (weekly)
 * @param {number} [attrs.weekCount] - contiguous tenant weeks a weekly plan
 *   covers (PLN-PERIOD-LADDER-001 V1 multi-week span; 1 / absent = single week)
 * @param {string} locationName - Location display name (caller passes 'Unknown'
 *   when no match).
 * @returns {string}
 */
function buildPlanName(attrs, locationName) {
  const loc = locationName;
  const type = capitalize(attrs.planType);
  const cat = capitalize(attrs.planCategory);
  // The month the period MEANS, which is `planMonth` everywhere except a retail
  // year anchored somewhere other than January (see the `displayMonth` param).
  const monthIndex = Number.isInteger(attrs.displayMonth) ? attrs.displayMonth : attrs.planMonth;

  if (attrs.planType === 'monthly') {
    return `${MONTHS[monthIndex - 1]} ${attrs.planYear} ${type} ${cat} — ${loc}`;
  }
  if (attrs.planType === 'quarterly') {
    return `Q${attrs.planQuarter} ${attrs.planYear} ${type} ${cat} — ${loc}`;
  }
  if (attrs.planType === 'annual') {
    return `${attrs.planYear} ${type} ${cat} — ${loc}`;
  }
  // Weekly (INITIAL_FORM's default is monthly; the wizard restricts planType to
  // the four branches, so this is the weekly case).
  // V1: a multi-week weekly plan names its whole ordinal range, anchored to the
  // start month ("Weeks 2–5, April 2026 …") — ordinals continue past the month
  // boundary exactly as the wizard's week math does. Anything that is not an
  // integer weekCount > 1 keeps the single-week name byte-identical.
  if (Number.isInteger(attrs.weekCount) && attrs.weekCount > 1) {
    const lastWeek = attrs.planWeek + attrs.weekCount - 1;
    return `Weeks ${attrs.planWeek}–${lastWeek}, ${MONTHS[monthIndex - 1]} ${attrs.planYear} ${type} ${cat} — ${loc}`;
  }
  return `Week ${attrs.planWeek}, ${MONTHS[monthIndex - 1]} ${attrs.planYear} ${type} ${cat} — ${loc}`;
}

module.exports = { buildPlanName };
